/**
 * Firestore Service
 * Replaces localStorage with Firebase Firestore for real database persistence
 */

import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Event } from '../types';

// Collection names
const EVENTS_COLLECTION = 'events';
const USER_EVENTS_COLLECTION = 'userEvents';
const TEMPLATES_COLLECTION = 'templates';

/**
 * Clean object by removing undefined values (Firestore doesn't accept undefined)
 */
function cleanObject(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(cleanObject);
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = cleanObject(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
}

/**
 * Save event to Firestore
 * NOTE: If ID starts with "template-", automatically saves to templates collection
 */
export async function saveEvent(event: Event): Promise<boolean> {
  try {
    // SAFETY: If this is a template (ID starts with "template-"), save to templates collection
    if (event.id.startsWith('template-') || event.isTemplate) {
      console.log('[saveEvent] Redirecting template to saveTemplate:', event.id);
      return saveTemplate(event);
    }
    
    const eventRef = doc(db, EVENTS_COLLECTION, event.id);
    // Clean the event to remove undefined values
    const cleanedEvent = cleanObject(event);
    await setDoc(eventRef, cleanedEvent);
    console.log('Event saved to Firestore:', event.id);
    return true;
  } catch (error) {
    console.error('Error saving event to Firestore:', error);
    return false;
  }
}

/**
 * Load event from Firestore (checks both events and templates collections)
 * Prioritizes templates collection for IDs starting with "template-"
 */
export async function loadEvent(eventId: string): Promise<Event | null> {
  try {
    // If ID starts with "template-", check templates collection FIRST
    // This prevents loading stale copies from events collection
    const isTemplateId = eventId.startsWith('template-');
    
    if (isTemplateId) {
      // Check templates collection first for template IDs
      const templateRef = doc(db, TEMPLATES_COLLECTION, eventId);
      const templateSnap = await getDoc(templateRef);
      
      if (templateSnap.exists()) {
        return templateSnap.data() as Event;
      }
    }
    
    // Try loading from events collection
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    
    if (eventSnap.exists()) {
      return eventSnap.data() as Event;
    }
    
    // If not a template ID and not found in events, try templates collection as fallback
    if (!isTemplateId) {
      const templateRef = doc(db, TEMPLATES_COLLECTION, eventId);
      const templateSnap = await getDoc(templateRef);
      
      if (templateSnap.exists()) {
        return templateSnap.data() as Event;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error loading event/template from Firestore:', error);
    return null;
  }
}

/**
 * Delete event from Firestore
 */
export async function deleteEvent(eventId: string): Promise<boolean> {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await deleteDoc(eventRef);
    console.log('Event deleted from Firestore:', eventId);
    return true;
  } catch (error) {
    console.error('Error deleting event from Firestore:', error);
    return false;
  }
}

/**
 * Check if event exists in Firestore
 */
export async function eventExists(eventId: string): Promise<boolean> {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnap = await getDoc(eventRef);
    return eventSnap.exists();
  } catch (error) {
    console.error('Error checking event existence:', error);
    return false;
  }
}

/**
 * Get all event IDs from Firestore
 */
export async function getAllEventIds(): Promise<string[]> {
  try {
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const querySnapshot = await getDocs(eventsRef);
    const eventIds: string[] = [];
    
    querySnapshot.forEach((doc) => {
      eventIds.push(doc.id);
    });
    
    console.log('Loaded event IDs from Firestore:', eventIds.length);
    return eventIds;
  } catch (error) {
    console.error('Error getting all event IDs:', error);
    return [];
  }
}

/**
 * Add event to user's events list
 */
export async function addEventToUser(userId: string, eventId: string): Promise<boolean> {
  try {
    const userEventRef = doc(db, USER_EVENTS_COLLECTION, userId);
    const userEventSnap = await getDoc(userEventRef);
    
    if (userEventSnap.exists()) {
      const data = userEventSnap.data();
      const events = data.events || [];
      if (!events.includes(eventId)) {
        events.push(eventId);
        await updateDoc(userEventRef, { events });
      }
    } else {
      await setDoc(userEventRef, { events: [eventId] });
    }
    
    console.log('Event added to user:', userId, eventId);
    return true;
  } catch (error) {
    console.error('Error adding event to user:', error);
    return false;
  }
}

/**
 * Get user's event IDs
 */
export async function getUserEvents(userId: string): Promise<string[]> {
  try {
    const userEventRef = doc(db, USER_EVENTS_COLLECTION, userId);
    const userEventSnap = await getDoc(userEventRef);
    
    if (userEventSnap.exists()) {
      const events = (userEventSnap.data().events || []) as string[];
      
      // Remove duplicates using Set (safety measure)
      const uniqueEvents = [...new Set<string>(events)];
      
      if (uniqueEvents.length !== events.length) {
        console.warn(`Found ${events.length - uniqueEvents.length} duplicate event IDs for user ${userId}, cleaning up`);
        // Update Firestore to remove duplicates
        await updateDoc(userEventRef, { events: uniqueEvents });
      }
      
      return uniqueEvents;
    }
    return [];
  } catch (error) {
    console.error('Error getting user events:', error);
    return [];
  }
}

/**
 * Subscribe to real-time event updates
 */
export function subscribeToEvent(eventId: string, callback: (event: Event | null) => void): Unsubscribe {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  
  return onSnapshot(eventRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data() as Event);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error subscribing to event:', error);
    callback(null);
  });
}

/**
 * Save current participant ID for an event (still use localStorage for this)
 * This is session-specific data that doesn't need to be in Firestore
 */
const USER_STORAGE_PREFIX = 'current-user-';

export function saveCurrentParticipant(eventId: string, participantId: string): boolean {
  try {
    const key = `${USER_STORAGE_PREFIX}${eventId}`;
    localStorage.setItem(key, participantId);
    return true;
  } catch (error) {
    console.error('Error saving current participant:', error);
    return false;
  }
}

export function getCurrentParticipant(eventId: string): string | null {
  try {
    const key = `${USER_STORAGE_PREFIX}${eventId}`;
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Error getting current participant:', error);
    return null;
  }
}

export function clearCurrentParticipant(eventId: string): boolean {
  try {
    const key = `${USER_STORAGE_PREFIX}${eventId}`;
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error clearing current participant:', error);
    return false;
  }
}

/**
 * TEMPLATE MANAGEMENT
 */

export async function getAllTemplates(): Promise<Event[]> {
  try {
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const querySnapshot = await getDocs(templatesRef);
    const templates: Event[] = [];
    
    querySnapshot.forEach((doc) => {
      templates.push(doc.data() as Event);
    });
    
    console.log('Loaded templates from Firestore:', templates.length);
    return templates;
  } catch (error) {
    console.error('Error getting all templates:', error);
    return [];
  }
}

export async function saveTemplate(event: Event): Promise<boolean> {
  try {
    const templateRef = doc(db, TEMPLATES_COLLECTION, event.id);
    const cleanedEvent = cleanObject({ ...event, isTemplate: true });
    
    // Debug: log fight results before saving
    console.log('[saveTemplate] Saving template:', event.id);
    console.log('[saveTemplate] Fights count:', cleanedEvent.fights?.length);
    cleanedEvent.fights?.forEach((f: any, i: number) => {
      console.log(`[saveTemplate] Fight ${i}: ${f.fighters?.[0]?.name} vs ${f.fighters?.[1]?.name}, status: ${f.status}, result:`, f.result);
    });
    
    await setDoc(templateRef, cleanedEvent);
    console.log('[saveTemplate] Template saved successfully to Firestore:', event.id);
    return true;
  } catch (error) {
    console.error('Error saving template to Firestore:', error);
    return false;
  }
}

export async function loadTemplate(templateId: string): Promise<Event | null> {
  try {
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (templateSnap.exists()) {
      return templateSnap.data() as Event;
    } else {
      console.log('Template not found in Firestore:', templateId);
      return null;
    }
  } catch (error) {
    console.error('Error loading template from Firestore:', error);
    return null;
  }
}

export async function deleteTemplate(templateId: string): Promise<boolean> {
  try {
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    await deleteDoc(templateRef);
    console.log('Template deleted from Firestore:', templateId);
    return true;
  } catch (error) {
    console.error('Error deleting template from Firestore:', error);
    return false;
  }
}

/**
 * PUBLIC EVENTS MANAGEMENT
 */
const PUBLIC_EVENTS_COLLECTION = 'publicEvents';

export async function addToPublicEvents(eventId: string): Promise<boolean> {
  try {
    const publicEventRef = doc(db, PUBLIC_EVENTS_COLLECTION, eventId);
    await setDoc(publicEventRef, { 
      eventId, 
      addedAt: Date.now() 
    });
    console.log('Event added to public events:', eventId);
    return true;
  } catch (error) {
    console.error('Error adding to public events:', error);
    return false;
  }
}

export async function removeFromPublicEvents(eventId: string): Promise<boolean> {
  try {
    const publicEventRef = doc(db, PUBLIC_EVENTS_COLLECTION, eventId);
    await deleteDoc(publicEventRef);
    console.log('Event removed from public events:', eventId);
    return true;
  } catch (error) {
    console.error('Error removing from public events:', error);
    return false;
  }
}

export async function getPublicEvents(): Promise<Event[]> {
  try {
    const publicEventsRef = collection(db, PUBLIC_EVENTS_COLLECTION);
    const querySnapshot = await getDocs(publicEventsRef);
    const eventIds: string[] = [];
    
    querySnapshot.forEach((doc) => {
      eventIds.push(doc.data().eventId);
    });
    
    // Load all events in PARALLEL for much faster loading
    const eventPromises = eventIds.map(async (eventId) => {
      try {
        let event = await loadEvent(eventId);
        const isPublicEvent = event && (event as any).isPublic === true;
        const isPublished = event && (event.published !== false); // Default to true if not set
        
        // Only show events that are public, published, and not finished
        if (event && isPublicEvent && isPublished && !event.finished) {
          // ALWAYS merge template fights if this is an event instance
          // (This ensures we get the latest template updates, even for old events)
          if (event.templateId) {
            const { mergeTemplateIntoEvent } = await import('../utils/templateMerge');
            event = await mergeTemplateIntoEvent(event);
          }
          return event;
        }
        return null;
      } catch (err) {
        console.error(`Failed to load public event ${eventId}:`, err);
        return null;
      }
    });
    
    // Wait for all events to load in parallel
    const loadedEventsWithNulls = await Promise.all(eventPromises);
    const events = loadedEventsWithNulls.filter((e): e is Event => e !== null);
    
    // Sort by participant count (descending)
    events.sort((a, b) => (b.participants?.length || 0) - (a.participants?.length || 0));
    
    console.log('Loaded public events:', events.length);
    return events;
  } catch (error) {
    console.error('Error getting public events:', error);
    return [];
  }
}

/**
 * Get events where user is admin
 */
export async function getUserAdminEvents(userId: string): Promise<Event[]> {
  try {
    const eventIds = await getUserEvents(userId);
    const events: Event[] = [];
    
    for (const eventId of eventIds) {
      const event = await loadEvent(eventId);
      if (event) {
        // Check if user is admin of this event
        const isAdmin = event.participants.some(p => p.id === event.adminId);
        if (isAdmin) {
          events.push(event);
        }
      }
    }
    
    return events;
  } catch (error) {
    console.error('Error getting admin events:', error);
    return [];
  }
}

/**
 * Get events where user is participating (but not admin)
 */
export async function getUserParticipantEvents(userId: string): Promise<Event[]> {
  try {
    const eventIds = await getUserEvents(userId);
    const events: Event[] = [];
    
    for (const eventId of eventIds) {
      const event = await loadEvent(eventId);
      if (event) {
        // Check if user is NOT admin of this event
        const isAdmin = event.participants.some(p => p.id === event.adminId);
        if (!isAdmin) {
          events.push(event);
        }
      }
    }
    
    return events;
  } catch (error) {
    console.error('Error getting participant events:', error);
    return [];
  }
}

/**
 * Finish a template and all events using that template
 */
export async function finishTemplateAndAllEvents(templateId: string): Promise<boolean> {
  try {
    const finishedAt = Date.now();
    
    // 1. Finish the template
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    await updateDoc(templateRef, {
      finished: true,
      finishedAt,
      status: 'completed',
      isAvailableForEvents: false, // No new events from finished templates
    });
    console.log('Template finished:', templateId);
    
    // 2. Find all events using this template
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const querySnapshot = await getDocs(eventsRef);
    
    const updatePromises: Promise<void>[] = [];
    
    querySnapshot.forEach((docSnap) => {
      const event = docSnap.data() as Event;
      if (event.templateId === templateId && !event.finished) {
        // Finish this event too
        const eventRef = doc(db, EVENTS_COLLECTION, docSnap.id);
        updatePromises.push(
          updateDoc(eventRef, {
            finished: true,
            finishedAt,
            status: 'completed',
          })
        );
      }
    });
    
    // Update all events in parallel
    await Promise.all(updatePromises);
    console.log(`Finished ${updatePromises.length} events using template ${templateId}`);
    
    return true;
  } catch (error) {
    console.error('Error finishing template and events:', error);
    return false;
  }
}

/**
 * Check if a template has any results (has started)
 */
export async function templateHasResults(templateId: string): Promise<boolean> {
  try {
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      return false;
    }
    
    const template = templateSnap.data() as Event;
    
    // Check if any fight has a result
    if (template.fights && template.fights.length > 0) {
      return template.fights.some(fight => 
        fight.status === 'completed' || fight.result !== undefined
      );
    }
    
    return false;
  } catch (error) {
    console.error('Error checking template results:', error);
    return false;
  }
}

